import {
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
} from "@chakra-ui/react";
import {
  BoltIcon,
  ClockIcon,
  MapPinIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import cx from "classnames";
import { formatDistanceToNowStrict } from "date-fns";
import React, { useEffect } from "react";
import JobLink from "../../components/JobLink";
import { trackCustomEvent } from "../../utils/analytics";

function SerpJobLinkHTML({ job, tx, cpaModalTx, hostedApplyTx, onClick }) {
  const { city, company, title, summary, date, isTopJob } = job;
  return (
    <JobLink
      gaCategory="SERP"
      cpaModalTx={cpaModalTx}
      hostedApplyTx={hostedApplyTx}
      job={job}
      className={cx("group block bg-white p-4 hover:bg-gray-50", {
        "rounded-md shadow-[0_0_3px_#aaa]": !isTopJob,
        "rounded-lg border border-accent-500 shadow-[0_0_6px_#999,3px_0_0_#D40063_inset]":
          isTopJob,
      })}
      onClick={onClick}
    >
      <div
        className={cx({
          "sm:flex sm:items-start sm:justify-between sm:gap-2": !isTopJob,
          "flex flex-col": isTopJob,
        })}
      >
        <div className="sm:flex sm:shrink sm:grow-0 sm:flex-col">
          <h3
            className={cx("font-medium capitalize text-primary-600", {
              "flex items-baseline text-lg": isTopJob,
              "text-md ": !isTopJob,
            })}
          >
            {isTopJob ? (
              <BoltIcon className="mr-2 w-4 text-accent-500" />
            ) : null}
            {title}
          </h3>
          {company && (
            <p
              className={cx("flex items-center text-sm text-gray-500 sm:mt-4", {
                "mt-4": isTopJob,
                "mt-2": !isTopJob,
              })}
            >
              <UsersIcon
                className="mr-1.5 h-5 w-5 flex-shrink-0 "
                aria-hidden="true"
              />
              {company}
            </p>
          )}
          {city && city !== "null" && (
            <p
              className={cx(
                "flex items-center text-sm capitalize text-gray-500 sm:mt-4",
                {
                  "mt-4": isTopJob,
                  "mt-2": !isTopJob,
                }
              )}
            >
              <MapPinIcon
                className="mr-1.5 h-5 w-5 flex-shrink-0"
                aria-hidden="true"
              />
              {city}
            </p>
          )}
          {date && (
            <p
              className={cx("flex items-center text-sm text-gray-500 sm:mt-4", {
                "mt-4": isTopJob,
                "mt-2": !isTopJob,
              })}
            >
              <ClockIcon className="mr-1.5 h-5 w-5" />
              {tx!.postedXago!.replace(
                "{{time}}",
                formatDistanceToNowStrict(new Date(date))
              )}
            </p>
          )}
          <p
            className={cx("block overflow-hidden text-sm text-gray-500 ", {
              "mt-6": isTopJob,
              "mt-4": !isTopJob,
            })}
          >
            {summary}
          </p>
        </div>
        <button
          className={cx(
            "flex w-full flex-shrink-0 items-center justify-center rounded-md border border-transparent  px-5 py-3 text-base font-medium text-white ",
            {
              "mt-8 bg-primary-600 hover:bg-primary-700": isTopJob,
              "mt-4 bg-primary-600 hover:bg-primary-700 sm:mt-0 sm:w-auto":
                !isTopJob,
            }
          )}
          tabIndex={-1}
        >
          {tx!.viewJob}
        </button>
      </div>
    </JobLink>
  );
}

export default function ExitIntentJobModal({
  location,
  position,
  tx,
}: {
  location: string;
  position: string;
  tx: any;
}) {
  const { isOpen, onClose } = useDisclosure({ defaultIsOpen: true });
  const hardcodedJobs = [
    {
      id: "1",
      city: location,
      company: "Posted by Talent.com",
      url: "https://uk.talent.com/jobs?k=&l=&source=dtl_bestjobs_ei&utm_source=dtl&subid=UK_SERP_EI",
      title: `${position} | Great Salary & Benefits`,
      summary: `Immediate Start. Urgently Hiring. Looking for ${position} Employees in Location Today!`,
      date: "2023-08-22",
      isTopJob: true,
    },
  ];

  useEffect(() => {
    trackCustomEvent({
      category: "SERP",
      action: `Exit intent modal load`,
      label: window.location.pathname,
    });
  }, []);

  const [job1] = hardcodedJobs;

  if (!job1) return null;
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent pb="4" rounded="xl" mx="6" maxW={"30rem"}>
        <ModalHeader>
          <Heading size="md" pt="4">
            {tx.exitIntentTitle}
          </Heading>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody
          display="flex"
          flexDirection="column"
          gap={{ base: "6", sm: "8" }}
        >
          {[job1].map((job) => (
            <SerpJobLinkHTML
              key={job.id + job.url}
              job={job}
              tx={tx}
              onClick={() => {
                trackCustomEvent({
                  category: "SERP",
                  action: `Job click from exit intent modal`,
                  label: window.location.pathname,
                });
              }}
            />
          ))}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
